import React from 'react';
import { toast } from 'sonner';

import {
  Box, ButtonBase, Checkbox, FormControlLabel, IconButton,
  makeStyles, Typography,
  useTheme,
} from '@material-ui/core';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import DeleteIcon from '@material-ui/icons/Delete';
import LibraryAddCheckRoundedIcon from '@material-ui/icons/LibraryAddCheckRounded';

import Button from 'creative-components/CustomButtons/Button';

import ActionModal from 'components/ActionModal/ActionModal';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';
import { useUserInfoContext } from 'components/UserInfoProvider/UserInfoProvider';

import { deleteUserPaymentMethod, updateUserDefaultPaymentMethod } from 'utils/api';
import { showAPIErrorAlert } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
    maxWidth: 300,
    height: 160,
    padding: '16px 24px',
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
  },
  icon: {
    marginRight: theme.spacing(2),
    fontSize: 36,
  },
  text: {
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 1.6,
  },
  isDefaultButton: {
    opacity: 1, // Don't reduce opacity since button is disabled
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginTop: '20px',

    '& > button': {
      borderRadius: '8px',
      padding: '8px 12px',
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '16px',
      textTransform: 'uppercase',
    },
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  cancelButton: {
    backgroundColor: `${theme.palette.grayScale6.main} !important`,
  },
  errorLink: {
    color: theme.palette.error.light,
    textDecoration: 'underline',
    fontWeight: 600,
  },
  updateExistingSubscriptionsControl: {
    alignItems: 'flex-start',
    width: '100%',
    margin: theme.spacing(3, 0, 1),

    '& .MuiCheckbox-root': {
      flexShrink: 0,
      marginTop: '2px',
      marginRight: theme.spacing(1.5),
    },
  },
  updateExistingSubscriptionsLabel: {
    color: theme.palette.darkGray.main,
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px',
  },
}));

interface Props {
  paymentMethodId: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

const PaymentMethodDisplay: React.FC<Props> = ({
  paymentMethodId,
  brand,
  last4,
  expMonth,
  expYear,
  isDefault,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const { reloadUserInfo } = useUserInfoContext();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext() as any;

  const [isConfirmingDelete, setIsConfirmingDelete] = React.useState(false);

  const [showConfirmDefaultModal, setShowConfirmDefaultModal] = React.useState(false);
  const [updateExistingSubscriptions, setUpdateExistingSubscriptions] = React.useState(false);

  const closeConfirmDefaultModal = () => {
    setShowConfirmDefaultModal(false);
    setUpdateExistingSubscriptions(false);
  };

  const openConfirmDefaultModal = () => {
    setUpdateExistingSubscriptions(false);
    setShowConfirmDefaultModal(true);
  };

  const showPaymentMethodUsageError = React.useCallback((usages: any[]) => {
    const firstUsage = usages[0];
    if (!firstUsage) return false;
    const additionalUsageCount = usages.length - 1;

    const usageLabel = firstUsage.type === 'ad-campaign' ? 'digital ad' : 'campaign';
    const usageRoute = firstUsage.type === 'ad-campaign'
      ? `/dashboard/ad-campaigns/${firstUsage.id}`
      : `/dashboard/campaigns/${firstUsage.id}`;

    toast.error(
      (
        <span>
          {`This payment method is currently being used by ${usageLabel} "${firstUsage.name}"${additionalUsageCount > 0 ? ` and ${additionalUsageCount} other item(s)` : ''}. Update it there before deleting this card. `}
          <a
            href={usageRoute}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.errorLink}
          >
            {`Open ${usageLabel}`}
          </a>
        </span>
      ),
      { duration: 8000 },
    );

    return true;
  }, [classes.errorLink]);

  const onMakeDefault = async (shouldUpdateExistingSubscriptions: boolean) => {
    try {
      showLoadingIndicatorModal();

      await updateUserDefaultPaymentMethod(paymentMethodId, shouldUpdateExistingSubscriptions);
      reloadUserInfo();

      if (shouldUpdateExistingSubscriptions) {
        toast.success('Your default payment method has been updated and all your existing campaigns and digital ads have been set to charge this credit card for future payments.');
      } else {
        toast.success('Your default payment method has been updated.');
      }
    } catch (err) {
      showAPIErrorAlert(err);
      console.error(err);
    } finally {
      hideLoadingIndicatorModal();
      closeConfirmDefaultModal();
    }
  };

  const onDelete = async () => {
    try {
      showLoadingIndicatorModal();

      await deleteUserPaymentMethod(paymentMethodId);
      reloadUserInfo();
    } catch (err: any) {
      if (!showPaymentMethodUsageError(err.response?.data?.paymentMethodUsages)) {
        showAPIErrorAlert(err);
      }
      console.error(err);
    } finally {
      setIsConfirmingDelete(false);
      hideLoadingIndicatorModal();
    }
  };

  return (
    <Box className={classes.root}>

      {isConfirmingDelete && (
        <ActionModal
          backgroundColor={theme.palette.offWhite.main}
          textColor={theme.palette.darkGray.main}
          icon={<LibraryAddCheckRoundedIcon />}
          onClose={() => setIsConfirmingDelete(false)}
          title="Delete Payment Method"
          message={`Are you sure you want to delete the card ending in ${last4}?`}
          buttons={[
            <ButtonBase onClick={() => setIsConfirmingDelete(false)} className={classes.cancelButton}>Cancel</ButtonBase>,
            <ButtonBase onClick={onDelete}>Delete</ButtonBase>,
          ]}
        />
      )}

      {showConfirmDefaultModal && (
        <ActionModal
          backgroundColor={theme.palette.offWhite.main}
          textColor={theme.palette.darkGray.main}
          icon={<LibraryAddCheckRoundedIcon />}
          onClose={closeConfirmDefaultModal}
          title="Change Default Payment Method"
          message={(
            <>
              <div>
                {'You are about to change your default payment method to the credit card ending in '}
                <span style={{ fontWeight: 600 }}>{last4}</span>
                . This card will be the default card used for all new subscriptions.
              </div>
              <FormControlLabel
                className={classes.updateExistingSubscriptionsControl}
                control={(
                  <Checkbox
                    checked={updateExistingSubscriptions}
                    color="primary"
                    onChange={(event) => setUpdateExistingSubscriptions(event.target.checked)}
                  />
                )}
                label={(
                  <span className={classes.updateExistingSubscriptionsLabel}>
                    {`Automatically update all existing campaigns and digital ads to use the credit card ending in ${last4}.`}
                  </span>
                )}
              />
            </>
          )}
          buttons={[
            <ButtonBase onClick={closeConfirmDefaultModal} className={classes.cancelButton}>
              Cancel
            </ButtonBase>,
            <ButtonBase onClick={() => onMakeDefault(updateExistingSubscriptions)}>
              Change
            </ButtonBase>,
          ]}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <CreditCardIcon className={classes.icon} />
        <div>
          <Typography variant="body1" className={classes.text}>
            {`${brand?.toUpperCase()} ending in ${last4}`}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {`Expires ${expMonth.toString().padStart(2, '0')}/${expYear}`}
          </Typography>
        </div>
      </div>

      <div className={classes.actions}>
        {isDefault ? (
          <Button color="primary" round disabled className={classes.isDefaultButton}>
            Default
          </Button>
        ) : (
          <>
            <Button color="dark" round onClick={openConfirmDefaultModal}>
              Make Default
            </Button>
            <IconButton onClick={() => setIsConfirmingDelete(true)} className={classes.deleteIcon}>
              <DeleteIcon />
            </IconButton>
          </>
        )}
      </div>
    </Box>
  );
};

export default PaymentMethodDisplay;
